import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import classnames from 'classnames'
import Rodal from 'rodal'

import useEventListener from '../../../utils/useEventListener'
import InlineError, { ariaErrorProps } from '../../../generic/InlineError'

import 'rodal/lib/rodal.css'
import '../../../generic/RenderedField/styles.css'
import './styles.css'

// Copy per variant. The course variant needs the certificate (its title and
// claim state); the general (All-Courses) variant only needs to know whether
// it is a first generation or a rename.
const copyFor = ({ variant, certificate, regenerate }) => {
  if (variant === 'general')
    return {
      heading: regenerate ? 'Edit Name' : 'All-Courses Certificate',
      body: regenerate ? (
        <p>
          Update the name printed on your All-Courses Certificate and
          we&apos;ll regenerate the PDF for you.
        </p>
      ) : (
        <p>
          How should your name appear on your All-Courses Certificate? It
          lists every course you&apos;ve completed.
        </p>
      ),
      submit: regenerate ? 'Regenerate Certificate' : 'Get My Certificate'
    }
  return {
    heading: certificate.isClaimed ? 'Edit Name' : 'Congratulations!',
    body: certificate.isClaimed ? (
      <p>
        Update the name printed on your certificate for{' '}
        <b>{certificate.tutorial.title}</b> and we&apos;ll regenerate the PDF
        for you.
      </p>
    ) : (
      <p>
        You watched all of <b>{certificate.tutorial.title}</b>. How should
        your name appear on your Certificate of Completion?
      </p>
    ),
    submit: certificate.isClaimed
      ? 'Regenerate Certificate'
      : 'Get My Certificate'
  }
}

function ClaimModal({
  variant = 'course',
  certificate = null,
  regenerate = false,
  defaultName = '',
  isMobile = false,
  onClose,
  onSubmit
}) {
  const [submitting, setSubmitting] = useState(false)
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors }
  } = useForm({ defaultValues: { name: defaultName } })

  // Focus (and select) the name field once the dialog is mounted so the user
  // can confirm or overwrite the prefilled name straight away. The rAF lets
  // Rodal finish its open animation before we take focus.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setFocus('name', { shouldSelect: true }))
    return () => cancelAnimationFrame(raf)
  }, [setFocus])

  const submit = (data) => {
    setSubmitting(true)
    return Promise.resolve(onSubmit(data)).finally(() => setSubmitting(false))
  }

  const escapeHandler = (e) => {
    if (e.key === 'Escape') onClose()
  }
  useEventListener('keydown', escapeHandler)

  const copy = copyFor({ variant, certificate, regenerate })
  const submitLabel = submitting ? 'Generating...' : copy.submit

  return (
    <Rodal
      visible
      onClose={onClose}
      width={isMobile ? 340 : 560}
      height={340}
      customStyles={{ height: 'auto', bottom: 'auto', top: '20%' }}
    >
      <div id="ClaimCertificateModal">
        <div className="modal-header">
          <span className="modal-badge" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path
                fill="currentColor"
                d="M12 2a5 5 0 0 0-3 9v0l-2 9 5-2.6L17 20l-2-9v0a5 5 0 0 0-3-9Zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z"
              />
            </svg>
          </span>
          <h2 className="title">{copy.heading}</h2>
        </div>
        {copy.body}
        <form onSubmit={handleSubmit(submit)}>
          <div
            className={classnames('field', '-floating', {
              '-error': errors.name
            })}
          >
            <div className="field-container">
              <input
                {...register('name', {
                  required: 'This field is required.',
                  maxLength: {
                    value: 255,
                    message: 'This name is too long.'
                  }
                })}
                type="text"
                placeholder=" "
                id="certificate-name"
                {...ariaErrorProps('certificate-name', errors.name)}
              />
              <label htmlFor="certificate-name">Your Name</label>
              <InlineError
                id="certificate-name"
                message={errors.name?.message}
              />
            </div>
          </div>
          <div className="action">
            <button
              type="submit"
              className="submit-btn btn -orange -mini"
              disabled={submitting}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </Rodal>
  )
}

const certificateShape = PropTypes.shape({
  tutorial: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  }).isRequired,
  isClaimed: PropTypes.bool
})

ClaimModal.propTypes = {
  variant: PropTypes.oneOf(['course', 'general']),
  // Required for the course variant, unused by the general one
  certificate: (props, propName, componentName) => {
    if (props.variant !== 'general' && !props[propName])
      return new Error(
        `${componentName}: \`certificate\` is required unless variant="general".`
      )
    return PropTypes.checkPropTypes(
      { [propName]: certificateShape },
      props,
      'prop',
      componentName
    )
  },
  regenerate: PropTypes.bool,
  defaultName: PropTypes.string,
  isMobile: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
}

export default ClaimModal
